import React, { useState } from 'react';
import { toast } from '../Toast';
import { X } from 'lucide-react';
import { AIAssistantModal } from '../AIAssistantModal';
import { EmailTemplateSelector } from '../EmailTemplateSelector';
import { DocumentTemplatesSelector } from '../DocumentTemplatesSelector';
import { crmApi } from '../../services/crmApi';
import { DocumentGeneratorPanel } from '../DocumentGeneratorPanel';
import { ComprehensiveAnalysisDisplay } from '../ComprehensiveAnalysisDisplay';
import { OptimizationReportModal } from '../OptimizationReportModal';
import { PurchaseListModal } from '../PurchaseListModal';
import { CertificateGeneratorModal } from '../CertificateGeneratorModal';
import { CertificateFormModal } from '../CertificateFormModal';
import { CourseSuggestionsModal } from '../CourseSuggestionsModal';
import { ClientLinkModal } from '../ClientLinkModal';
import { LoadingOverlay } from '../ProgressIndicator';
import { ProductAnalysisDisplay } from './ProductAnalysisDisplay';
import { MarketingGenerator } from '../MarketingGenerator';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface Props { h: ReturnType<typeof useApplicationDetail>; }

export const DetailModals: React.FC<Props> = ({ h }) => {
  const {
    id, application,
    showAIAssistant, showEmailTemplates, showDocumentTemplates, showDocumentGenerator,
    showClientLinkModal, showComprehensiveAnalysis, comprehensiveAnalysis,
    showOptimizationReport, optimizationReport,
    showPurchaseList, purchaseListReport,
    showCertificates, certificatePackage,
    showCertificateForm,
    showCourseRecommendations, courseRecommendations,
    generatingOptimization, generatingPurchaseList, generatingCertificates,
    generatingCourseRecommendations, generatingAI, generatingPUPApplication,
    generatingPDF, sendingEmail,
    showProductAnalysis, productAnalysis,
    showProductActionResult, productActionResult, generatingProductAction,
    showMarketingGenerator,
  } = h;

  if (!application) return null;

  return (
    <>
      {/* AI Assistant Modal */}
      <AIAssistantModal
        isOpen={showAIAssistant}
        onClose={() => h.setShowAIAssistant(false)}
        candidateContext={h.getCandidateContext()}
        applicationId={application?.id}
      />

      {/* Client Link Modal */}
      {showClientLinkModal && (
        <ClientLinkModal
          applicationId={application.id}
          onClose={() => h.setShowClientLinkModal(false)}
        />
      )}

      {/* Email Template Selector */}
      {showEmailTemplates && (
        <EmailTemplateSelector
          applicationId={application.id}
          serviceType={application.service_type}
          onClose={() => h.setShowEmailTemplates(false)}
          onSend={async (templateId, preview) => {
            try {
              await crmApi.sendTemplateEmail(application.id, {
                subject: preview.subject,
                html: preview.html,
                templateId,
              });
              toast.info(`Email "${preview.subject}" wysłany do ${application.email}`);
              h.setShowEmailTemplates(false);
            } catch (err: any) {
              toast.error(`Błąd wysyłania: ${err.response?.data?.error || err.message}`);
            }
          }}
        />
      )}

      {/* Document Templates Selector */}
      {showDocumentTemplates && (
        <DocumentTemplatesSelector
          applicationId={application.id}
          onClose={() => h.setShowDocumentTemplates(false)}
        />
      )}

      {/* Document Generator Panel */}
      {showDocumentGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => h.setShowDocumentGenerator(false)} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold z-10">×</button>
            <div className="p-6">
              <DocumentGeneratorPanel
                applicationId={application.id}
                serviceType={application.service_type}
              />
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive AI Analysis Modal */}
      {showComprehensiveAnalysis && comprehensiveAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
            <ComprehensiveAnalysisDisplay
              analysis={comprehensiveAnalysis}
              onClose={() => h.setShowComprehensiveAnalysis(false)}
            />
          </div>
        </div>
      )}

      {/* Optimization Report Modal */}
      {showOptimizationReport && (
        <OptimizationReportModal
          report={optimizationReport}
          applicationId={parseInt(id!)}
          onClose={() => h.setShowOptimizationReport(false)}
        />
      )}

      {/* Purchase List Modal */}
      {showPurchaseList && (
        <PurchaseListModal
          report={purchaseListReport as any}
          onClose={() => h.setShowPurchaseList(false)}
        />
      )}

      {/* Certificate Generator Modal */}
      {showCertificates && (
        <CertificateGeneratorModal
          package={certificatePackage as any}
          onClose={() => h.setShowCertificates(false)}
        />
      )}

      {/* Certificate Form Modal with AI */}
      {showCertificateForm && (
        <CertificateFormModal
          applicationId={parseInt(id || '0')}
          onClose={() => h.setShowCertificateForm(false)}
          onGenerate={h.handleGenerateWithFormData}
        />
      )}

      {/* Course Suggestions Modal */}
      {showCourseRecommendations && (
        <CourseSuggestionsModal
          recommendations={courseRecommendations as any}
          onClose={() => h.setShowCourseRecommendations(false)}
          onGenerateCertificate={h.handleGenerateCourseCertificate}
        />
      )}

      {/* Product Analysis Modal (generic, non-PUP) */}
      {showProductAnalysis && productAnalysis && (
        <ProductAnalysisDisplay
          analysis={productAnalysis}
          onClose={() => h.setShowProductAnalysis(false)}
        />
      )}

      {/* Product AI Action Result Modal */}
      {showProductActionResult && productActionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-[#0f2a4d] to-[#1e4d7a] text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{productActionResult.title || 'Wynik akcji AI'}</h2>
                <p className="text-sm text-blue-200 mt-1">Wygenerowano przez AI</p>
              </div>
              <button onClick={() => h.setShowProductActionResult(false)} className="p-2 hover:bg-white/20 rounded-lg transition text-white">
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              {productActionResult.score && (
                <div className={`text-center p-4 rounded-xl border-2 ${productActionResult.score >= 70 ? 'bg-emerald-50 border-emerald-300' : productActionResult.score >= 40 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'}`}>
                  <div className={`text-4xl font-black ${productActionResult.score >= 70 ? 'text-emerald-600' : productActionResult.score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                    {productActionResult.score}<span className="text-xl">/100</span>
                  </div>
                </div>
              )}
              {productActionResult.content && (
                <div className="bg-gray-50 rounded-xl p-5 border">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{productActionResult.content}</div>
                </div>
              )}
              {productActionResult.recommendations?.length > 0 && (
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-3">Rekomendacje ({productActionResult.recommendations.length})</h3>
                  <ul className="space-y-2">
                    {productActionResult.recommendations.map((r: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-900">
                        <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
              <span className="text-xs text-gray-400">Wygenerowano przez AI — wynik może wymagać weryfikacji</span>
              <button onClick={() => h.setShowProductActionResult(false)} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] font-medium text-sm">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Marketing Generator */}
      {showMarketingGenerator && (
        <MarketingGenerator
          applicationId={application.id}
          productCode={application.service_type}
          onClose={() => h.setShowMarketingGenerator(false)}
        />
      )}

      {/* Loading Overlays */}
      <LoadingOverlay show={generatingProductAction} message="Wykonywanie akcji AI..." submessage="AI generuje treść dopasowaną do produktu. Czas: 10-20 sekund." />
      <LoadingOverlay show={generatingOptimization} message="Generowanie raportu optymalizacji..." submessage="AI analizuje lokalizację, konkurencję i generuje konkretne sugestie. Czas: 15-30 sekund." />
      <LoadingOverlay show={generatingPurchaseList} message="Generowanie listy zakupów z linkami..." submessage="AI analizuje branżę i przygotowuje szczegółową listę zakupów. Czas: 15-25 sekund." />
      <LoadingOverlay show={h.generatingForecast} message="Generowanie prognozy finansowej..." submessage="AI tworzy 3-letnią prognozę: przychody, koszty, break-even, cashflow. Czas: 15-25 sekund." />
      <LoadingOverlay show={h.generatingTaxAdvice} message="Analiza podatkowa..." submessage="AI porównuje ryczałt, KPiR, pełną księgowość i rekomenduje optymalną formę. Czas: 10-20 sekund." />
      <LoadingOverlay show={h.generatingMarketResearch} message="Badanie rynku..." submessage="AI analizuje konkurencję, grupę docelową, trendy i strategię cenową. Czas: 15-25 sekund." />

      {/* Financial Forecast Modal */}
      {h.showFinancialForecast && h.financialForecast && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white p-5 flex justify-between items-center">
              <h2 className="text-xl font-bold">Prognoza Finansowa 3Y</h2>
              <button onClick={() => h.setShowFinancialForecast(false)} className="p-2 hover:bg-white/20 rounded-lg transition text-white"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-gray-800">{h.financialForecast.summary}</p>
              </div>
              {/* KPIs */}
              {h.financialForecast.kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">ROI Rok 1</div><div className="text-2xl font-bold text-blue-700">{h.financialForecast.kpis.roi_year1_pct}%</div></div>
                  <div className="bg-green-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Break-even</div><div className="text-2xl font-bold text-green-700">{h.financialForecast.breakeven_month} mies.</div></div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Zysk/m-c Y1</div><div className="text-2xl font-bold text-purple-700">{h.financialForecast.kpis.avg_monthly_profit_y1?.toLocaleString('pl-PL')} zł</div></div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Zysk/m-c Y3</div><div className="text-2xl font-bold text-amber-700">{h.financialForecast.kpis.avg_monthly_profit_y3?.toLocaleString('pl-PL')} zł</div></div>
                </div>
              )}
              {/* P&L Table */}
              {h.financialForecast.profitability && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Rachunek zysków i strat</h3>
                  <table className="w-full text-sm border-collapse">
                    <thead><tr className="bg-gray-100"><th className="p-2 text-left">Rok</th><th className="p-2 text-right">Przychody</th><th className="p-2 text-right">Koszty</th><th className="p-2 text-right">Zysk</th><th className="p-2 text-right">Marża</th></tr></thead>
                    <tbody>
                      {['year1','year2','year3'].map((y,i) => {
                        const d = h.financialForecast.profitability[y];
                        if (!d) return null;
                        return <tr key={y} className="border-b"><td className="p-2 font-medium">Rok {i+1}</td><td className="p-2 text-right text-green-700">{d.revenue?.toLocaleString('pl-PL')} zł</td><td className="p-2 text-right text-red-600">{d.costs?.toLocaleString('pl-PL')} zł</td><td className={`p-2 text-right font-bold ${d.profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{d.profit?.toLocaleString('pl-PL')} zł</td><td className="p-2 text-right">{d.margin_pct}%</td></tr>;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Cashflow Chart (text) */}
              {h.financialForecast.cashflow_year1 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Cashflow Y1 (saldo kumulatywne)</h3>
                  <div className="flex items-end gap-1 h-32 bg-gray-50 rounded-xl p-3">
                    {h.financialForecast.cashflow_year1.map((v: number, i: number) => {
                      const max = Math.max(...h.financialForecast.cashflow_year1.map(Math.abs));
                      const h2 = max ? Math.abs(v) / max * 100 : 10;
                      return <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className={`w-full rounded-t ${v >= 0 ? 'bg-emerald-400' : 'bg-red-400'}`} style={{height: `${Math.max(h2, 5)}%`}} />
                        <span className="text-[9px] text-gray-500">{i+1}</span>
                      </div>;
                    })}
                  </div>
                </div>
              )}
              {/* Cost Structure */}
              {h.financialForecast.cost_structure?.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Struktura kosztów</h3>
                  <div className="space-y-2">
                    {h.financialForecast.cost_structure.map((c: any, i: number) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-32 text-sm text-gray-700 truncate">{c.name}</div>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{width: `${c.pct}%`}} /></div>
                        <div className="text-sm font-medium w-20 text-right">{c.monthly?.toLocaleString('pl-PL')} zł</div>
                        <div className="text-xs text-gray-500 w-12 text-right">{c.pct}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Tax Recommendation */}
              {h.financialForecast.tax_recommendation && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="font-bold text-amber-800 mb-2">Rekomendacja podatkowa</h3>
                  <p className="text-sm text-gray-700">{h.financialForecast.tax_recommendation}</p>
                </div>
              )}
              {/* Risks & Opportunities */}
              <div className="grid md:grid-cols-2 gap-4">
                {h.financialForecast.risks?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <h3 className="font-bold text-red-800 mb-2">Ryzyka</h3>
                    <ul className="space-y-1">{h.financialForecast.risks.map((r: string, i: number) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-red-500">!</span>{r}</li>)}</ul>
                  </div>
                )}
                {h.financialForecast.opportunities?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h3 className="font-bold text-green-800 mb-2">Szanse rozwoju</h3>
                    <ul className="space-y-1">{h.financialForecast.opportunities.map((o: string, i: number) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-green-500">+</span>{o}</li>)}</ul>
                  </div>
                )}
              </div>
              {/* Assumptions */}
              {h.financialForecast.assumptions?.length > 0 && (
                <details className="bg-gray-50 rounded-xl p-4"><summary className="font-bold text-gray-700 cursor-pointer">Założenia ({h.financialForecast.assumptions.length})</summary>
                  <ul className="mt-2 space-y-1">{h.financialForecast.assumptions.map((a: string, i: number) => <li key={i} className="text-sm text-gray-600">• {a}</li>)}</ul>
                </details>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <span className="text-xs text-gray-400">Prognoza AI — wymaga weryfikacji przez doradcę</span>
              <button onClick={() => h.setShowFinancialForecast(false)} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] font-medium">Zamknij</button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Advisor Modal */}
      {h.showTaxAdvice && h.taxAdvice && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white p-5 flex justify-between items-center">
              <h2 className="text-xl font-bold">Doradca Podatkowy AI</h2>
              <button onClick={() => h.setShowTaxAdvice(false)} className="p-2 hover:bg-white/20 rounded-lg transition text-white"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Recommendation */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 text-center">
                <div className="text-sm text-gray-600 mb-1">Rekomendowana forma opodatkowania</div>
                <div className="text-3xl font-black text-amber-700">{h.taxAdvice.recommendation_label}</div>
                <div className="text-sm text-gray-600 mt-2">{h.taxAdvice.reasoning}</div>
              </div>
              {/* Comparison */}
              {h.taxAdvice.comparison?.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Porównanie form opodatkowania</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {h.taxAdvice.comparison.map((c: any, i: number) => (
                      <div key={i} className={`rounded-xl border-2 p-4 ${c.form === h.taxAdvice.recommendation ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-bold text-gray-800">{c.label}</h4>
                          <span className={`text-lg font-black ${c.score >= 7 ? 'text-green-600' : c.score >= 4 ? 'text-amber-600' : 'text-red-600'}`}>{c.score}/10</span>
                        </div>
                        <div className="text-sm text-gray-600 mb-2">Stawka: {c.rate} | ~{c.monthly_tax_estimate?.toLocaleString('pl-PL')} zł/m-c</div>
                        {c.pros?.length > 0 && <div className="mb-1">{c.pros.map((p: string, j: number) => <span key={j} className="inline-block text-xs bg-green-100 text-green-700 rounded px-2 py-0.5 mr-1 mb-1">+ {p}</span>)}</div>}
                        {c.cons?.length > 0 && <div>{c.cons.map((p: string, j: number) => <span key={j} className="inline-block text-xs bg-red-100 text-red-700 rounded px-2 py-0.5 mr-1 mb-1">- {p}</span>)}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* ZUS Info */}
              {h.taxAdvice.zus_info && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-bold text-blue-800 mb-2">Składki ZUS</h3>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div><strong>Ulga na start:</strong> {h.taxAdvice.zus_info.ulga_na_start}</div>
                    <div><strong>Mały ZUS:</strong> {h.taxAdvice.zus_info.maly_zus}</div>
                    <div><strong>Pełny ZUS:</strong> {h.taxAdvice.zus_info.pelny_zus}</div>
                    <div><strong>Zdrowotna:</strong> {h.taxAdvice.zus_info.zdrowotna}</div>
                  </div>
                </div>
              )}
              {/* VAT */}
              {h.taxAdvice.vat_recommendation && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <h3 className="font-bold text-purple-800 mb-2">VAT</h3>
                  <p className="text-sm">{h.taxAdvice.vat_recommendation.register ? 'Zalecana rejestracja jako VAT-owiec' : 'Zwolnienie z VAT'} — {h.taxAdvice.vat_recommendation.reasoning}</p>
                </div>
              )}
              {/* Savings */}
              {h.taxAdvice.first_year_savings && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <h3 className="font-bold text-green-800 mb-2">Szacunkowe oszczędności w 1. roku</h3>
                  <div className="text-3xl font-black text-green-700">{h.taxAdvice.first_year_savings.total_estimated_savings?.toLocaleString('pl-PL')} zł</div>
                </div>
              )}
              {/* Warnings */}
              {h.taxAdvice.warnings?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="font-bold text-orange-800 mb-2">Ostrzeżenia</h3>
                  <ul className="space-y-1">{h.taxAdvice.warnings.map((w: string, i: number) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-orange-500">!</span>{w}</li>)}</ul>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <span className="text-xs text-gray-400">Porada AI — skonsultuj z księgowym</span>
              <button onClick={() => h.setShowTaxAdvice(false)} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] font-medium">Zamknij</button>
            </div>
          </div>
        </div>
      )}

      {/* Market Research Modal */}
      {h.showMarketResearch && h.marketResearch && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5 flex justify-between items-center">
              <h2 className="text-xl font-bold">Raport Rynkowy AI</h2>
              <button onClick={() => h.setShowMarketResearch(false)} className="p-2 hover:bg-white/20 rounded-lg transition text-white"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Market Size */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-violet-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Rynek lokalny</div><div className="text-lg font-bold text-violet-700">{h.marketResearch.market_size}</div></div>
                <div className="bg-blue-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Trend</div><div className="text-lg font-bold text-blue-700">{h.marketResearch.growth_trend} ({h.marketResearch.growth_pct})</div></div>
                <div className="bg-orange-50 rounded-xl p-4 text-center"><div className="text-xs text-gray-500">Nasycenie</div><div className="text-lg font-bold text-orange-700">{h.marketResearch.competition?.saturation} (~{h.marketResearch.competition?.estimated_competitors_local} firm)</div></div>
              </div>
              {/* Target Audience */}
              {h.marketResearch.target_audience && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-bold text-blue-800 mb-2">Grupa docelowa</h3>
                  <p className="text-sm mb-1"><strong>Główna:</strong> {h.marketResearch.target_audience.primary}</p>
                  <p className="text-sm"><strong>Drugorzędna:</strong> {h.marketResearch.target_audience.secondary}</p>
                </div>
              )}
              {/* SWOT */}
              {h.marketResearch.swot && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Analiza SWOT</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3"><h4 className="font-bold text-green-700 text-sm mb-1">Mocne strony</h4>{h.marketResearch.swot.strengths?.map((s: string, i: number) => <p key={i} className="text-xs text-gray-700">+ {s}</p>)}</div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3"><h4 className="font-bold text-red-700 text-sm mb-1">Słabe strony</h4>{h.marketResearch.swot.weaknesses?.map((s: string, i: number) => <p key={i} className="text-xs text-gray-700">- {s}</p>)}</div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3"><h4 className="font-bold text-blue-700 text-sm mb-1">Szanse</h4>{h.marketResearch.swot.opportunities?.map((s: string, i: number) => <p key={i} className="text-xs text-gray-700">+ {s}</p>)}</div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3"><h4 className="font-bold text-orange-700 text-sm mb-1">Zagrożenia</h4>{h.marketResearch.swot.threats?.map((s: string, i: number) => <p key={i} className="text-xs text-gray-700">! {s}</p>)}</div>
                  </div>
                </div>
              )}
              {/* Pricing + Marketing */}
              {h.marketResearch.pricing_strategy && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <h3 className="font-bold text-emerald-800 mb-2">Strategia cenowa</h3>
                  <p className="text-sm">{h.marketResearch.pricing_strategy.recommended}</p>
                  <p className="text-xs text-gray-500 mt-1">Zakres cen: {h.marketResearch.pricing_strategy.price_range} | Pozycjonowanie: {h.marketResearch.pricing_strategy.positioning}</p>
                </div>
              )}
              {/* Marketing Channels */}
              {h.marketResearch.marketing_channels?.length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Kanały marketingowe</h3>
                  <div className="space-y-2">
                    {h.marketResearch.marketing_channels.map((ch: any, i: number) => (
                      <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                        <span className={`px-2 py-1 text-xs rounded font-bold ${ch.effectiveness === 'wysoka' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{ch.effectiveness}</span>
                        <div className="flex-1"><div className="text-sm font-medium">{ch.channel}</div><div className="text-xs text-gray-500">{ch.description}</div></div>
                        <div className="text-sm font-medium text-gray-700">~{ch.monthly_cost} zł/m-c</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Trends */}
              {h.marketResearch.trends?.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <h3 className="font-bold text-purple-800 mb-2">Trendy rynkowe</h3>
                  <ul className="space-y-1">{h.marketResearch.trends.map((t: string, i: number) => <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-purple-500">~</span>{t}</li>)}</ul>
                </div>
              )}
              {/* Recommendation */}
              <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-4">
                <h3 className="font-bold text-indigo-800 mb-2">Rekomendacja strategiczna</h3>
                <p className="text-sm text-gray-800">{h.marketResearch.recommendation}</p>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <span className="text-xs text-gray-400">Raport AI — szacunki mogą wymagać weryfikacji</span>
              <button onClick={() => h.setShowMarketResearch(false)} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] font-medium">Zamknij</button>
            </div>
          </div>
        </div>
      )}
      <LoadingOverlay show={generatingCertificates} message="Generowanie zaświadczeń i dokumentów..." submessage="AI tworzy profesjonalne zaświadczenia. Czas: 20-30 sekund." />
      <LoadingOverlay show={generatingCourseRecommendations} message="Analizowanie profilu i dopasowywanie kursów..." submessage="AI przegląda 15 kursów Fundacji NH. Czas: 15-20 sekund." />
      <LoadingOverlay show={generatingAI} message="Generowanie analizy AI..." submessage="Ocena 23-punktowa, sprawdźanie błędów, analiza rynku (~5-10 sekund)" />
      <LoadingOverlay show={generatingPUPApplication} message="Generowanie wniosku PUP..." submessage="AI tworzy kompletny wniosek (2000-4000 słów). Może to potrwać 15-30 sekund." />
      <LoadingOverlay show={generatingPDF} message="Generowanie PDF..." submessage="Konwersja do PDF z formatowaniem. Proszę czekać..." />
      <LoadingOverlay show={sendingEmail} message="Wysyłanie emaila..." submessage="Email jest wysyłany. Preview URL otwórzy się automatycznie." />
    </>
  );
};
